import AudioPlayerV2 from "@/components/audio/exerciseV2/AudioPlayerV2";
import ChevronRightLarge from "@/components/icons/ChevronRightLarge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { ExerciseDefinitionsItem } from "@/types";
import parse from "html-react-parser";
import { FC, useState } from "react";

const BASE = import.meta.env.EDS_BASE_URL;

interface ExerciseTrackProps {
  audioUrl?: string;
  imageUrl?: string;
  exerciseType: string;
  definitions?: ExerciseDefinitionsItem[];
  exerciseTrack?: string;
}

interface DomNode {
  type: string;
  name?: string;
  data?: string;
  children?: DomNode[];
}

interface ReplaceOptions {
  replace: (domNode: DomNode) => React.ReactNode | undefined;
}

function processExerciseText(
  text: string,
  definitions: Array<{ value: string; definition: string }> | undefined,
) {
  const options: ReplaceOptions = {
    replace: (domNode: DomNode) => {
      if (
        domNode.type === "tag" &&
        domNode.name === "strong" &&
        domNode.children &&
        domNode.children.length > 0
      ) {
        const childNode = domNode.children[0];

        if (childNode.data) {
          const cleanWord = childNode.data.trim();

          const definition = definitions?.find(
            (def) => cleanWord.toLowerCase() === def.value.toLowerCase(),
          );

          if (definition) {
            return (
              <Tooltip key={childNode.data}>
                <TooltipTrigger asChild>
                  <strong>{childNode.data}</strong>
                </TooltipTrigger>

                <TooltipContent
                  side="top"
                  align="center"
                  className="bg-brand text-white text-xs"
                >
                  {definition.definition}
                </TooltipContent>
              </Tooltip>
            );
          }

          return <strong>{childNode.data}</strong>;
        }
      }

      return undefined;
    },
  };
  //@ts-expect-error varying options
  return parse(text, options);
}

const ExerciseTrack: FC<ExerciseTrackProps> = ({
  audioUrl,
  imageUrl,
  definitions,
  exerciseType,
  exerciseTrack,
}) => {
  const [isTextVisible, setTextVisible] = useState(false);

  const processedText = exerciseTrack
    ? processExerciseText(exerciseTrack, definitions)
    : [];

  const toggleTextVisibility = () => setTextVisible(!isTextVisible);

  const audioSrc = audioUrl ? `${BASE}/${audioUrl}` : "";
  const imgSrc = imageUrl ? `${BASE}/${imageUrl}` : "";

  if (["reading", "grammar"].includes(exerciseType) && !audioUrl && !imageUrl) {
    return (
      <TooltipProvider>
        <div className="h-auto text-xs italic font-normal overflow-auto">
          {processedText}
        </div>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {Boolean(audioUrl) && (
        <TooltipProvider>
          <div className="flex flex-col gap-1">
            <div className="flex justify-between">
              <div className="font-semibold text-xs">
                Listen to the audio and answer the following questions:
              </div>

              {!imageUrl && (
                <button onClick={toggleTextVisibility}>
                  <ChevronRightLarge
                    style={{
                      transform: `rotate(${isTextVisible ? "270" : "90"}deg)`,
                    }}
                    className="fill-brand-secondary w-4 h-4"
                  />
                </button>
              )}
            </div>

            <AudioPlayerV2 src={audioSrc} />

            {exerciseTrack && isTextVisible && !imageUrl && (
              <div className="text-xs italic font-normal mt-2 pb-2 h-[150px] overflow-auto">
                {processedText}
              </div>
            )}
          </div>
        </TooltipProvider>
      )}

      {Boolean(imageUrl) && (
        <TooltipProvider>
          <div className="flex flex-col gap-2">
            <div className="font-semibold text-xs">
              Remember and follow this rule:
            </div>

            <div className="flex justify-between gap-5">
              <img
                src={imgSrc}
                alt="Grammar Exercise"
                className="max-w-[400px] max-h-[200px] shadow-lg"
              />

              <div className="h-[200px] text-xs italic font-normal overflow-auto">
                {processedText}
              </div>
            </div>
          </div>
        </TooltipProvider>
      )}
    </div>
  );
};

export default ExerciseTrack;
